import { useEffect, useState } from 'react';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C3c §14b] A DEBOUNCED VALUE, AND THE FIRST SHARED ONE IN `web/src`.
 *
 *  ⚠️ IT DEBOUNCES THE VALUE, NOT THE REQUEST — which is what keeps TanStack Query working.
 *
 *  The returned value feeds a QUERY KEY, so the cache, `staleTime` and `signal` cancellation
 *  all behave exactly as they did: a repeated term is served from cache, and an in-flight
 *  request for a superseded term is aborted by the library. Debouncing the `queryFn` instead
 *  breaks both halves — a request that has not been issued cannot be cancelled, and a key that
 *  still changes per keystroke defeats the cache it was meant to protect.
 *  `AssociatedUnitsPicker` already debounces "in the query key, not in a timer"; this is that
 *  idea with a name.
 *
 *  ⚠️ IT IS `hooks/`, NOT `lib/`. It holds a timer and a `useState`; `lib/` is pure.
 *  `useColumnVisibility`'s own placement argument.
 *
 *  ─── Two callers it deliberately does NOT have ───────────────────────────────────
 *
 *  ⚠️ `useServerTable` IS NOT A CONSUMER AND MUST NOT BECOME ONE. It debounces the URL WRITE,
 *  through nuqs's own `debounce()` at 300 ms — a different fact (how often the address bar
 *  changes) about a different value. Its header calls that "debounced ONCE, in the URL layer,
 *  so there is not a second timer that can drift out of step with the first", and folding it in
 *  here would create exactly that second timer.
 *
 *  ⚠️ AND `components/fill/OfficerSearchSheet.tsx` KEEPS ITS OWN, WHICH IS NOT AN OVERSIGHT.
 *  That sheet clears its debounced term SYNCHRONOUSLY, in a render-time adjustment, when the
 *  cursor advances to the next slot — otherwise the previous slot's term stays live for one
 *  debounce window and fires a candidate query for the wrong row. A hook that owns its own
 *  timer cannot be reset from outside without a second parameter that would exist for one
 *  caller, so the extraction stops at two and says so rather than growing generality nobody
 *  else needs.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** Long enough to stop a burst of requests, short enough that nobody waits for it. */
export const SEARCH_DEBOUNCE_MS = 200;

export const useDebounced = <T>(value: T, delayMs: number = SEARCH_DEBOUNCE_MS): T => {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSettled(value);
    }, delayMs);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delayMs]);

  return settled;
};
